import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin, Gem, Trophy, Award, Medal } from "lucide-react";
import VenueCard from "@/components/VenueCard";
import { tierFor } from "@/components/TierBadge";
import { CATEGORIES } from "@/data/constants";

const TIERS = ["Diamond", "Platinum", "Gold", "Silver", "Bronze"];
const TIER_ICONS = { Diamond: Gem, Platinum: Trophy, Gold: Award, Silver: Medal, Bronze: Medal };

export default function AreaLanding() {
  const { name } = useParams();
  const decoded = decodeURIComponent(name || "");
  const [venues, setVenues] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/venues?area=${encodeURIComponent(decoded)}`).then(r => setVenues(r.data)).finally(() => setLoading(false));
    api.get("/meta/areas").then(r => setAreas(r.data));
  }, [decoded]);

  const validArea = areas.length === 0 || areas.some(a => a.toLowerCase() === decoded.toLowerCase());

  const byTier = TIERS.reduce((acc, t) => { acc[t] = []; return acc; }, {});
  venues.forEach(v => {
    const t = v.tier || tierFor(v.rating);
    if (byTier[t]) byTier[t].push(v);
  });
  const byCategory = CATEGORIES.map(c => ({ ...c, count: venues.filter(v => v.category === c.slug).length }))
                              .filter(c => c.count > 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-blue-700"><ArrowLeft className="w-4 h-4"/>Home</Link>
      <div className="mt-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-blue-600 text-white flex items-center justify-center"><MapPin className="w-6 h-6"/></div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-gray-900">{decoded}</h1>
      </div>
      {!validArea ? (
        <div className="mt-8 border-2 border-dashed border-gray-300 rounded-xl p-10 text-center">
          <p className="text-lg text-gray-700">No area named "{decoded}" yet.</p>
          <Button asChild className="mt-4 rounded-full bg-blue-600 hover:bg-blue-700"><Link to="/search">Back to search</Link></Button>
        </div>
      ) : (
        <>
          <p className="mt-3 text-lg text-gray-700 max-w-2xl">
            {loading ? "Loading…" :
              venues.length === 0 ? `No graded places in ${decoded} yet — be the first to nominate one.` :
              <><strong className="text-gray-900">{venues.length}</strong> accessible {venues.length === 1 ? "place" : "places"} in {decoded}, inspected and graded by our community.</>}
          </p>

          {venues.length > 0 && (
            <>
              <div className="mt-6 flex flex-wrap gap-2">
                {TIERS.map(t => byTier[t].length > 0 && (
                  <a key={t} href={`/search?area=${encodeURIComponent(decoded)}&min_tier=${t.toLowerCase()}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-gray-300 text-sm hover:border-gray-900" data-testid={`area-tier-${t.toLowerCase()}`}>
                    <span>{t}</span>
                    <span className="text-gray-500">· {byTier[t].length}</span>
                  </a>
                ))}
              </div>

              {byCategory.length > 0 && (
                <Card className="mt-6 rounded-xl border border-gray-200">
                  <CardContent className="p-5">
                    <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-3">In this area</div>
                    <div className="flex flex-wrap gap-2">
                      {byCategory.map(c => (
                        <Link key={c.slug} to={`/search?category=${c.slug}&area=${encodeURIComponent(decoded)}`}
                          className="inline-flex items-center gap-1 text-sm border border-gray-200 hover:border-gray-900 rounded-full px-3 py-1">
                          {c.short} <span className="text-gray-500">· {c.count}</span>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {TIERS.map(t => byTier[t].length === 0 ? null : (
                <section key={t} className="mt-12">
                  <h2 className="font-heading text-2xl font-semibold text-gray-900 flex items-center gap-2">
                    {React.createElement(TIER_ICONS[t], { className: "w-5 h-5", "aria-hidden": true })}
                    {t}
                    <Badge variant="outline" className="rounded-full ml-1">{byTier[t].length}</Badge>
                  </h2>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {byTier[t].map(v => <VenueCard key={v.id} venue={v}/>)}
                  </div>
                </section>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}
